"use client";

import { Fragment, MutableRefObject, useState } from "react";
import { useRouter } from "next/navigation";
import { Dialog, Transition } from "@headlessui/react";
import { FaHeadset } from "react-icons/fa";
import { GiCharacter } from "react-icons/gi";
import { Character } from "schema";

import { trpc } from "~/app/providers";
import { LineBucket } from "../page";
import SelectDropDown from "./CharacterSelect";

export default function CreateLineModal({
  characters,
  stageHands,
  tail,
  insert,
  close,
}: {
  characters: Character[];
  stageHands: Character[];
  tail?: number;
  insert?: {
    pos: "before" | "after";
    node: LineBucket;
  };
  close?: (
    focusableElement?:
      | HTMLElement
      | MutableRefObject<HTMLElement | null>
      | undefined,
  ) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [formData, setFormData] = useState({
    play: characters[0].playId,
    character: characters[0].id,
    lineText: "",
  });

  const [activePage, setActivePage] = useState(1);

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setFormData((prevState) => ({
      ...prevState,
      lineText: e.target.value,
    }));
  };

  const handleCharacterChange = (newCharacter: Character) => {
    setFormData((prevFormData) => ({
      ...prevFormData,
      character: newCharacter.id,
    }));
  };

  const router = useRouter();

  const createLine = trpc.lines.createLine.useMutation({
    onSuccess: () => {
      router.refresh();
    },
  });

  const insertLineBefore = trpc.lines.insertLineBefore.useMutation({
    onSuccess: () => {
      router.refresh();
    },
  });

  const insertLineAfter = trpc.lines.insertLineAfter.useMutation({
    onSuccess: () => {
      router.refresh();
    },
  });

  const insertLineFront = trpc.lines.insertLineFront.useMutation({
    onSuccess: () => {
      router.refresh();
    },
  });

  function closeModal() {
    setFormData((prevFormData) => ({
      ...prevFormData,
      character: characters[0].id,
    }));
    setIsOpen(false);

    if (close) {
      close();
    }
  }

  function openModal() {
    setFormData((prevFormData) => ({
      ...prevFormData,
      character: characters[0].id,
    }));
    setActivePage(1);
    setIsOpen(true);
  }

  function onClick() {
    if (insert?.pos === "before") {
      if (insert?.node.prev === null) {
        insertLineFront.mutate({
          playId: formData.play,
          text: formData.lineText,
          characterId: formData.character,
          node: {
            next: insert.node.next,
            prev: insert.node.prev,
            id: insert.node.id,
          },
        });
      } else {
        insertLineBefore.mutate({
          playId: formData.play,
          text: formData.lineText,
          characterId: formData.character,
          node: {
            next: insert.node.next,
            prev: insert.node.prev,
            id: insert.node.id,
          },
        });
      }
    } else if (insert?.pos === "after") {
      if (insert?.node.next === null) {
        createLine.mutate({
          playId: formData.play,
          text: formData.lineText,
          characterId: formData.character,
          prevBucket: insert.node.bucketId,
        });
      } else {
        insertLineAfter.mutate({
          playId: formData.play,
          text: formData.lineText,
          characterId: formData.character,
          node: {
            next: insert.node.next,
            prev: insert.node.prev,
            id: insert.node.id,
          },
        });
      }
    } else {
      createLine.mutate({
        playId: formData.play,
        text: formData.lineText,
        characterId: formData.character,
        prevBucket: tail,
      });
    }

    closeModal();
  }

  return (
    <>
      {(() => {
        if (insert?.pos === "before") {
          return (
            <button
              className="flex w-full items-center gap-1 px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900"
              onClick={openModal}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="size-6"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="m4.5 15.75 7.5-7.5 7.5 7.5"
                />
              </svg>
              Add Line Before
            </button>
          );
        } else if (insert?.pos === "after") {
          return (
            <button
              className="flex w-full items-center gap-1 px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900"
              onClick={openModal}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="size-6"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="m19.5 8.25-7.5 7.5-7.5-7.5"
                />
              </svg>
              Add Line After
            </button>
          );
        } else {
          return (
            <div className="flex items-center justify-center pb-12">
              <button
                className="mt-12 rounded-lg bg-indigo-50 px-4 py-4 text-indigo-600 duration-150 hover:bg-indigo-100 active:bg-indigo-200"
                onClick={openModal}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  className="h-6 w-6"
                >
                  <path
                    fillRule="evenodd"
                    d="M12 5.25a.75.75 0 01.75.75v5.25H18a.75.75 0 010 1.5h-5.25V18a.75.75 0 01-1.5 0v-5.25H6a.75.75 0 010-1.5h5.25V6a.75.75 0 01.75-.75z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            </div>
          );
        }
      })()}

      <Transition appear show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={closeModal}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-7xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <div className="flex items-center align-middle">
                    {activePage === 1 && (
                      <div>
                        <label className="block text-lg font-medium text-gray-800">
                          Characters
                        </label>
                        <SelectDropDown
                          characters={characters}
                          onChange={handleCharacterChange}
                          newLine={true}
                        />
                      </div>
                    )}
                    {activePage === 2 && (
                      <div>
                        <label className="block text-lg font-medium text-gray-800">
                          Stage Direction
                        </label>
                        <SelectDropDown
                          characters={stageHands}
                          onChange={handleCharacterChange}
                          newLine={true}
                        />
                      </div>
                    )}
                    <div className="ml-10 mr-10 flex flex-col gap-4">
                      <button
                        className={activePage === 1 ? "text-amber-600" : ""}
                        onClick={() => {
                          setActivePage(1);
                          setFormData((prevState) => ({
                            ...prevState,
                            character: characters[0].id,
                          }));
                        }}
                      >
                        <GiCharacter className="h-6 w-6" />
                      </button>
                      <button
                        className={activePage === 2 ? "text-amber-600" : ""}
                        onClick={() => {
                          setActivePage(2);
                          setFormData((prevState) => ({
                            ...prevState,
                            character: stageHands[0].id,
                          }));
                        }}
                      >
                        <FaHeadset className="h-6 w-6" />
                      </button>
                    </div>
                  </div>
                  <div className="mt-8">
                    <label className="block text-lg font-medium text-gray-800">
                      Line
                      <textarea
                        onChange={handleTextChange}
                        className="mt-2 w-full rounded-lg border-slate-200 align-top shadow-sm focus-within:border-slate-300 focus-within:ring-slate-300 sm:text-sm"
                        rows={4}
                      ></textarea>
                    </label>
                  </div>

                  <div className="mr-4 mt-6 flex justify-end">
                    <button
                      type="button"
                      className="inline-flex justify-center rounded-md border border-transparent bg-blue-100 px-4 py-2 text-sm font-medium text-blue-900 hover:bg-blue-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                      onClick={onClick}
                    >
                      Add Line
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
}
